/**
 * Strategy Brief CTA card — top of "The Meal" right column.
 * Routes the user to /strategy-brief?analysis_id=<latest> when they have at
 * least one tailored resume. Otherwise shows a locked state.
 */
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Sparkles, ArrowRight, Gauge, ChevronRight } from "lucide-react";
import { api } from "../../lib/api";

export default function StrategyBriefCard({ makeover }) {
  const navigate = useNavigate();
  const [latestBrief, setLatestBrief] = useState(null);
  const locked = !makeover;

  // Show the most recent brief inline if one exists for the latest analysis.
  useEffect(() => {
    if (!makeover?.analysis_id) return undefined;
    let alive = true;
    (async () => {
      try {
        const { data } = await api.get("/strategy-brief");
        const hit = (data?.briefs || []).find((b) => b.analysis_id === makeover.analysis_id);
        if (alive && hit) setLatestBrief(hit);
      } catch { /* non-fatal */ }
    })();
    return () => { alive = false; };
  }, [makeover?.analysis_id]);

  const onClick = () => {
    if (locked) return;
    if (latestBrief?.id) {
      navigate(`/strategy-brief/${latestBrief.id}`);
    } else {
      navigate(`/strategy-brief?analysis_id=${makeover.analysis_id}`);
    }
  };

  return (
    <div
      className="rm-card-static p-4 md:p-5"
      style={{
        background: "linear-gradient(135deg, #0F1B2D 0%, #1E3A5F 100%)",
        color: "white",
        boxShadow: "6px 6px 0 0 #FFC145",
        opacity: locked ? 0.7 : 1,
      }}
      data-testid="strategy-brief-card"
    >
      <div className="flex items-start gap-3 flex-wrap">
        <div className="flex-shrink-0 flex items-center justify-center"
             style={{ width: 44, height: 44, borderRadius: 10, background: "#FFC145", color: "#0F1B2D" }}>
          <Sparkles size={20} strokeWidth={2.4} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-mono-rm uppercase text-[10px] tracking-widest mb-0.5"
               style={{ color: "#FFC145", fontWeight: 700 }}>
            · NEW · Strategic AI briefing
          </div>
          <div className="font-display uppercase leading-tight"
               style={{ fontSize: "1.15rem", letterSpacing: "-0.02em" }}>
            Build a Role Strategy Brief.
          </div>
          <p className="text-[12.5px] mt-1.5 leading-snug" style={{ color: "#CBD5E1" }}>
            {locked
              ? "Tailor a Job Description first — then Resmo consolidates your fit, gaps, network, and superpower into one strategic page."
              : "One page. Problem statement → business currency → superpower play → ATS gaps → fit score → next step. ~60-90 seconds."}
          </p>
        </div>
      </div>

      {latestBrief?.fit_score != null && (
        <div className="flex items-center gap-2 mt-3 p-2 rounded"
             style={{ background: "rgba(255,193,69,0.12)", border: "2px solid #FFC145" }}
             data-testid="strategy-brief-card-fit">
          <Gauge size={13} style={{ color: "#FFC145" }} />
          <span className="font-mono-rm uppercase text-[10px] tracking-widest" style={{ color: "#FFC145", fontWeight: 700 }}>
            Last brief · Fit
          </span>
          <span className="font-display text-base" style={{ color: "#FFC145" }}>{latestBrief.fit_score}/100</span>
        </div>
      )}

      <button
        type="button"
        onClick={onClick}
        disabled={locked}
        className="w-full flex items-center justify-center gap-2 px-4 py-2.5 text-[13px] font-semibold transition-transform hover:-translate-y-0.5 mt-3"
        style={{
          background: locked ? "#475569" : "#FFC145",
          color: locked ? "#94A3B8" : "#0F1B2D",
          border: "none",
          borderRadius: 8,
          cursor: locked ? "not-allowed" : "pointer",
          boxShadow: locked ? "none" : "0 4px 12px rgba(255,193,69,0.35)",
        }}
        data-testid="strategy-brief-card-cta"
      >
        {locked ? (
          <>Locked · need a Job Description first <ChevronRight size={13} /></>
        ) : latestBrief ? (
          <>View your strategy brief <ArrowRight size={13} /></>
        ) : (
          <>Build my strategy brief · 10 credits <ArrowRight size={13} /></>
        )}
      </button>
    </div>
  );
}
